import {FormIdType, HouseType, HouseTypology, MPRNDetails} from '@/types/selections';
import {useRecommendations} from './recommendations';
import {GammaAPIService} from '@/service/gamma-api.service';
import {defineStore, storeToRefs} from 'pinia';
import {
  FormType,
  GetTypologyByBerResponse,
  GetTypologyByUprnResponse,
  PostLookupResult,
  RequestError,
  UKCountryCode
} from '@/types';
import {useLoan} from './loan';
import {useSettings} from './settings';
import {BerwowEvent, emit, TrackingEvent} from '@/helpers';
import {useBerRatings} from "./map-ber-rating";
import {toRaw} from "vue";
import {t} from '@/helpers/locale';

interface SelectionState {
    selectedFormType: FormType | undefined,
    houseTypologies: HouseTypology[],
    houseTypeSelected: HouseType | undefined,
    houseTypologySelected: HouseTypology | undefined,
    submitted: boolean,
    age: number;
    noAge: boolean;
    storeys: number;
    termsAccepted: boolean | undefined;
    mprn: string | undefined;
    mprnValid: boolean | undefined;
    mprnError: RequestError | undefined;
    mprnDetails: MPRNDetails | undefined;
    fileUploaded: boolean;
    mprnFile: File | undefined;
    addressResult: PostLookupResult | undefined;
    submitLoading: boolean;
    berData: GetTypologyByBerResponse | undefined;
    countryCode: UKCountryCode | undefined;
    bedrooms: number | undefined;
    settingsLoaded: boolean;
    epcData: GetTypologyByUprnResponse | undefined;
    uprn: number | undefined;
}

export const useSelections = defineStore('selections', {
    state: (): SelectionState => ({
        selectedFormType: undefined,
        houseTypologies: [],
        houseTypeSelected: undefined,
        houseTypologySelected: undefined,
        submitted: false,
        age: 1950,
        noAge: false,
        storeys: -1,
        termsAccepted: undefined,
        mprn: undefined,
        mprnValid: undefined,
        mprnError: undefined,
        fileUploaded: false,
        mprnFile: undefined,
        mprnDetails: undefined,
        addressResult: undefined,
        submitLoading: false,
        berData: undefined,
        countryCode: undefined,
        bedrooms: undefined,
        settingsLoaded: false,
        epcData: undefined,
        uprn: undefined,
    }),
    getters: {
        filteredHouseTypes: (state) => [...state.houseTypologies.reduce((map, typology) => {
            if (
                (state.noAge || typology.ageBand.from <= state.age + 9) &&
                (state.noAge || typology.ageBand.to >= state.age) &&
                (state.storeys == -1 || typology.storeys === state.storeys) &&
                (state.bedrooms == undefined || state.bedrooms === typology.bedrooms) &&
                (state.countryCode == undefined || state.countryCode == typology.countryCode)
            ) {
                const houseType = {
                    text: typology.description,
                    value: typology.houseType.toString(),
                    ageBands: [typology.ageBand],
                };
                const existing = map.get(houseType.value);
                if (existing) {
                    existing.ageBands.push(...houseType.ageBands);
                } else {
                    map.set(houseType.value, houseType);
                }
            }
            return map;
        }, new Map<string, HouseType>()).values()],
        houseTypes: (state) => [...state.houseTypologies.reduce((map, typology) => {
            if (
                (state.noAge || typology.ageBand.from <= state.age + 9) &&
                (state.noAge || typology.ageBand.to >= state.age)
            ) {
                const houseType = {
                    text: typology.description,
                    value: typology.houseType.toString(),
                    ageBands: [typology.ageBand],
                };
                const existing = map.get(houseType.value);
                if (existing) {
                    existing.ageBands.push(...houseType.ageBands);
                } else {
                    map.set(houseType.value, houseType);
                }
            }
            return map;
        }, new Map<string, HouseType>()).values()],
        filteredHouseTypologies: (state) => {
            const typology = state.houseTypologies.filter((typology) =>
                (state.noAge || typology.ageBand.from <= state.age + 9) &&
                (state.noAge || typology.ageBand.to >= state.age) &&
                (typology.storeys === state.storeys || state.storeys === -1) &&
                (!state.bedrooms || state.bedrooms == typology.bedrooms) &&
                (!state.countryCode || state.countryCode == typology.countryCode) &&
                state.houseTypeSelected &&
                typology.houseType === state.houseTypeSelected.value);
            if (typology.length === 1) state.houseTypologySelected = typology[0];
            else state.houseTypologySelected = undefined;
            return typology;
        },
        options: (state) => {
            switch (state.selectedFormType) {
                case FormType.MPRN:
                    return {
                        file: state.mprnFile,
                        id: state.mprn,
                        idType: FormIdType.MPRN
                    };

                case FormType.BER:
                    return {
                        id: state.berData?.berDetails?.berNumber,
                        idType: FormIdType.BER,
                        details: state.houseTypologySelected ? state.houseTypologySelected.code : state.berData?.typology.code
                    };

                case FormType.Address:
                    return {
                        id: state.uprn ? state.uprn.toString() : "-1",
                        idType: FormIdType.AddressUK,
                        details: state.houseTypologySelected?.code
                    };

                default:
                    return {
                        id: state.houseTypologySelected?.code,
                        idType: FormIdType.TypicalHome
                    };
            }
        },
        addressInfo: (state) => {
            const addressResult = toRaw(state.addressResult);
            if (addressResult) {
                if (addressResult.address.lines.length < 3) {
                    const missing = 3 - addressResult.address.lines.length;
                    for (let index = 0; index < missing; index++) {
                        addressResult.address.lines.push({key: 'AddressLine' + (index + missing), value: ''});
                    }
                }
            }
            return addressResult ? {
                addressId: addressResult.data?.ie_building?.ecadId,
                result: addressResult.message.id,
                matchLevel: addressResult.data?.ie_location?.accuracy,
                addressType: addressResult.data?.location?.accuracy,
                ...addressResult.address
            } : undefined;
        },
        storeysAvailable: (state) => {
            const available = state.houseTypologies.filter(t =>
                state.noAge || (t.ageBand.from <= state.age + 9 && t.ageBand.to >= state.age)
            );
            return {
                one: available.some(t => t.storeys === 1),
                twoPlus: available.some(t => t.storeys && t.storeys > 1)
            };
        }
    },
    actions: {
        async submitForm(quereyRecommendations = true) {
            this.submitLoading = true;
            const recommendationsStore = useRecommendations();
            const berRatingsStore = useBerRatings();
            const loanOpts = useSettings().settings?.loanOptions;

            if (this.addressResult?.data?.location) {
                const {latitude, longitude} = this.addressResult.data.location;
                await berRatingsStore.loadBerRatingByCoords(latitude, longitude);
            }

            this.emitSubmit();

            if (quereyRecommendations) await recommendationsStore.queryRecommendations(this.options);
            await useLoan().addLoanOptions(loanOpts ? loanOpts : [], true);

            await recommendationsStore.applyRecommendations(true, true); // Apply loanOptionSelected

            this.submitted = true;
            this.submitLoading = false;
        },
        emitSubmit() {
            const {clientSessionId} = storeToRefs(useSettings());
            const formTypeLabelDict = new Map<FormType, string>([
                [FormType.TypicalHome, t("selections.typicalIrishHomes")],
                [FormType.MPRN, t("selections.useMPRN")],
                [FormType.BER, t("selections.useBER")]
            ]);
            emit(BerwowEvent.TrackingEvent, {
                event: TrackingEvent.QuestionareInformationSent,
                homerecommendations: this.selectedFormType ? formTypeLabelDict.get(this.selectedFormType) : undefined,
                agreement: "Yes",
                ageofhome: this.age,
                address: this.addressInfo,
                storeys: this.storeys == 1 ? '1' : '2+',
                houseType: this.houseTypeSelected,
                houseTypology: this.houseTypologySelected?.description,
                timestamp: Date.now(),
                clientSessionId: clientSessionId.value,
            });
        },
        setAddressResult(address: PostLookupResult) {
            this.addressResult = address;
            this.uprn = address?.data?.gb_identifiers?.uprn ? address.data.gb_identifiers.uprn : -1;
        },
        emptyAddressResult() {
            this.addressResult = undefined;
        },
        async setSelectedFormType(formType: FormType | undefined) {
            this.resetForms();
            this.termsAccepted = undefined;
            this.selectedFormType = formType;
        },
        async getHouseTypologies() {
            this.houseTypologies = await GammaAPIService.getHouseTypologies();
        },
        setHouseTypologySelected(typology: HouseTypology | undefined) {
            this.houseTypologySelected = typology;
        },
        async validateMPRN() {
            const response = await GammaAPIService.validateMPRN({
                mprn: this.mprn,
                timestamp: Date.now()
            });

            this.mprnValid = !!response.isValid;
            this.mprnError = response;
            this.mprnDetails = response.berDetails ?? undefined;
        },
        resetForms() {
            this.emptyAddressResult();
            this.noAge = false;
            this.age = 1950;
            this.bedrooms = undefined;
            this.storeys = -1;
            this.berData = undefined;
            this.uprn = undefined;
            this.epcData = undefined;
            this.resetMPRN();
            this.resetTypeAndTypology();
        },
        resetMPRN() {
            this.mprn = undefined;
            this.mprnValid = undefined;
            this.fileUploaded = false;
            this.mprnFile = undefined;
        },
        setFile(file: File) {
            this.mprnFile = file;
            this.fileUploaded = true;
        },
        resetTypeAndTypology() {
            this.termsAccepted = undefined;
            this.houseTypeSelected = undefined;
            this.houseTypologySelected = undefined;
        },
        resetMPRNValidity() {
            this.mprnError = undefined;
            this.mprnValid = undefined;
        },
        setSubmitLoading(loading: boolean) {
            this.submitLoading = loading;
        },
        async loadBerData(berNumber: number) {
            if (!this.houseTypologies.length) await this.getHouseTypologies();
            this.setHouseTypologySelected(undefined);
            this.berData = undefined;
            this.berData = await GammaAPIService.getTypologiesByBER({
                berNumber
            });

            const ageN = this.berData.typology.ageBand.from + (this.berData.typology.ageBand.to - this.berData.typology.ageBand.from) / 2;
            this.age = Math.floor(ageN / 10) * 10;
            this.storeys = this.berData.typology.storeys ? this.berData.typology.storeys : -1;
            this.houseTypeSelected = this.filteredHouseTypes.find((type: HouseType) => type.value === this.berData?.typology.houseType);
            this.setHouseTypologySelected(this.filteredHouseTypologies.find((typology: HouseTypology) => typology.code === this.berData?.typology.code));
        },
        async loadEpcData() {
            if (!this.houseTypologies.length) await this.getHouseTypologies();

            this.epcData = await GammaAPIService.getTypologiesByUPRN({
                uprn: this.uprn,
                address: this.addressResult?.address.label
            });

            const ageN = this.epcData.typology.ageBand.from + (this.epcData.typology.ageBand.to - this.epcData.typology.ageBand.from) / 2;
            this.age = Math.floor(ageN / 10) * 10;
            this.bedrooms = this.epcData.typology.bedrooms;
            this.countryCode = this.epcData.typology.countryCode;
            this.houseTypeSelected = this.filteredHouseTypes.find((type: HouseType) => type.value === this.epcData?.typology.houseType);
            this.setHouseTypologySelected(this.filteredHouseTypologies.find((typology: HouseTypology) => typology.code === this.epcData?.typology.code));
        },
        clearMRPNFile() {
            this.fileUploaded = false;
            this.mprnFile = undefined;
        },
        unsubmit() {
            this.submitted = false;
            this.submitLoading = false;
        }
    },
});
